import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import {
  WithPermissions,
  registerResource,
  unregisterResource,
  ResourceProps,
  ResourceMatch,
  ReduxState,
  ResourceDefinition,
  ResourceContextProvider
} from 'react-admin';
import { useProfile } from '../../../hooks/UseProfile';

const defaultOptions = {};

export interface NewAdminResourceProps {
  permissions: string[];
  readPermission: string;
  createPermission: string;
  editPermission: string;
  deletePermission: string;
}

const ResourceRegister = (props: ResourceProps & NewAdminResourceProps) => {
  const {
    name,
    permissions,
    readPermission,
    createPermission,
    editPermission,
    deletePermission,
    list,
    create,
    edit,
    show,
    icon,
    options = defaultOptions
  } = props;
  const dispatch = useDispatch();
  useEffect(() => {
    const hasReadPermission = permissions
      ? permissions.includes(readPermission)
      : true;
    const hasCreatePermission = permissions
      ? permissions.includes(createPermission)
      : true;
    const hasEditPermission = permissions
      ? permissions.includes(editPermission)
      : true;
    const hasDeletePermission = permissions
      ? permissions.includes(deletePermission)
      : true;

    // Conditions added to prevent adding the resource to the left menu if user has not read permission
    dispatch(
      registerResource({
        name,
        options,
        hasList: hasReadPermission ? !!list : undefined,
        hasEdit: hasReadPermission ? !!edit : undefined,
        hasShow: hasReadPermission ? !!show : undefined,
        hasCreate: hasCreatePermission ? !!create : undefined,
        icon,
        hasCreatePermission,
        hasEditPermission,
        hasDeletePermission
      } as ResourceDefinition)
    );
    return () => {
      dispatch(unregisterResource(name));
    };
  }, [
    dispatch,
    name,
    permissions,
    readPermission,
    createPermission,
    editPermission,
    deletePermission,
    create,
    edit,
    icon,
    list,
    show,
    options
  ]);
  return null;
};

const ResourceRoutes = (props: ResourceProps & NewAdminResourceProps) => {
  const {
    name,
    permissions,
    readPermission,
    createPermission,
    editPermission,
    deletePermission,
    match,
    list,
    create,
    edit,
    show,
    options = defaultOptions
  } = props;
  const isRegistered = useSelector(
    (state: ReduxState) => !!state.admin.resources[name]
  );
  const { profile } = useProfile();

  const basePath = match ? match.path : '';

  const resourceData = useMemo(() => {
    const hasReadPermission = permissions
      ? permissions.includes(readPermission)
      : true;
    const hasCreatePermission = permissions
      ? permissions.includes(createPermission)
      : true;
    const hasEditPermission = permissions
      ? permissions.includes(editPermission)
      : true;
    const hasDeletePermission = permissions
      ? permissions.includes(deletePermission)
      : true;

    return {
      resource: name,
      options,
      hasList: !!list,
      hasEdit: !!edit,
      hasShow: !!show,
      hasCreate: !!create,
      hasReadPermission,
      hasCreatePermission,
      hasEditPermission,
      hasDeletePermission,
      userProfile: profile
    };
  }, [
    name,
    options,
    create,
    edit,
    list,
    show,
    permissions,
    readPermission,
    createPermission,
    editPermission,
    deletePermission,
    profile
  ]);

  // match tends to change even on the same route ; using memo to avoid an extra render
  return useMemo(() => {
    // if the registration hasn't finished, no need to render
    if (!isRegistered) {
      return null;
    }

    // Conditions added to prevent creating routes for the resource if user has not the permission
    return (
      <ResourceContextProvider value={name}>
        <Switch>
          {create && resourceData.hasCreatePermission && (
            <Route
              path={`${basePath}/create`}
              render={routeProps => (
                <WithPermissions
                  component={create}
                  basePath={basePath}
                  {...routeProps}
                  {...resourceData}
                />
              )}
            />
          )}
          {show && resourceData.hasReadPermission && (
            <Route
              path={`${basePath}/:id/show`}
              render={routeProps => (
                <WithPermissions
                  component={show}
                  basePath={basePath}
                  id={decodeURIComponent(
                    (routeProps.match as ResourceMatch).params.id
                  )}
                  {...routeProps}
                  {...resourceData}
                />
              )}
            />
          )}
          {edit && resourceData.hasReadPermission && (
            <Route
              path={`${basePath}/:id`}
              render={routeProps => (
                <WithPermissions
                  component={edit}
                  basePath={basePath}
                  id={decodeURIComponent(
                    (routeProps.match as ResourceMatch).params.id
                  )}
                  {...routeProps}
                  {...resourceData}
                />
              )}
            />
          )}
          {list && resourceData.hasReadPermission && (
            <Route
              path={`${basePath}`}
              render={routeProps => (
                <WithPermissions
                  component={list}
                  basePath={basePath}
                  {...routeProps}
                  {...resourceData}
                />
              )}
            />
          )}
        </Switch>
      </ResourceContextProvider>
    );
  }, [basePath, name, resourceData, create, edit, list, show, isRegistered]);
};

const Resource = (props: ResourceProps & NewAdminResourceProps) => {
  const { intent = 'route', ...rest } = props;
  return intent === 'registration' ? (
    <ResourceRegister {...rest} />
  ) : (
    <ResourceRoutes {...rest} />
  );
};

export default Resource;
